import { FC, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isEqual } from 'lodash';

import SectionDivider from 'components/pages/Planner/SectionDivider';
import { changeNewProductField, hideSecondaryPanel } from 'store/productManagement/reducer';

import { CircularLoadingSpinner } from 'lib/CircularLoadingSpinner/CircularLoadingSpinner';
import Search from 'lib/Search';
import { Store } from 'components/common/types/Store.types';
import { CodeNameModel } from 'components/common/types';
import { useCategoryOptions } from 'customHooks/useCategoryOptions';
import { canDisplayOptionResults } from 'components/pages/Planner/PlannerSecondaryPanel/utils';
import LocationOptionsList from 'components/pages/Planner/PlannerSections/FiltersContent/Location/LocationOptionsList';
import {
  transformLocationToCategories,
  transformSelectedLocationsToCodeNameModels,
  transformCodeNameModelsToSelectedLocations,
  ORDERED_DSM_LOCATION_CATEGORY,
} from 'components/pages/Planner/PlannerSections/FiltersContent/Location/utils';
import { ProductFieldName } from 'store/productManagement/reducer.types';

import { secondaryPanelLayoutStyles } from '../../styles';
import { isValueRequired } from '../../utils/isValueRequired';
import { isPanelConfirmDisabled } from '../../utils/isPanelConfirmDisabled';
import { permissionOptions } from '../../consts';
import SelectionPermissionDropdown from '../SelectionPermissionDropdown/SelectionPermissionDropdown';
import { ProductLocationsProps } from './ProductLocations.types';
import { ProductSecondaryPanel } from '../ProductSecondaryPanel/ProductSecondaryPanel';

const ProductLocationsPanel: FC<ProductLocationsProps> = ({ onCancel, isReadOnly = false }) => {
  const dispatch = useDispatch();

  const productLocations = useSelector((state: Store) => state.productManagement.newProduct?.locations);
  const isFetchingReferences = useSelector((state: Store) => state.dealManagement.isFetchingReferences);
  const locationOptions = useSelector((state: Store) => state.userOptions.referenceOptions.location);
  const defaultSelectedPermission =
    permissionOptions.find(({ code }) => code === productLocations?.selectionPermission) ?? permissionOptions[0];

  const [selectedPermission, setSelectedPermission] = useState<CodeNameModel>(defaultSelectedPermission);

  const fieldName = ProductFieldName.LOCATIONS;

  const categoryOptions = useMemo(() => transformLocationToCategories(locationOptions), [locationOptions]);
  const initialSelectedOptions = useMemo(
    () => transformSelectedLocationsToCodeNameModels(productLocations?.value ?? []),
    [productLocations?.value],
  );

  const {
    onCategoryDirectionSort,
    searchText,
    openCategories,
    onFilterTextChange,
    selectedOptions,
    filteredOptions,
    getNumberOfFilteredOptions,
    sortDirection,
    includedOptions,
    excludedOptions,
    isOptionSelected,
    isOptionIncluded,
    onOptionIncludeExcludeChange,
    onToggleCategory,
    setSelectedOptions,
    onCategoryIncludeAll,
    onCategoryExcludeAll,
    getCategoryIncludeState,
  } = useCategoryOptions({
    options: categoryOptions,
    initialSelectedOptions,
    categoryOrder: ORDERED_DSM_LOCATION_CATEGORY,
  });

  const isChange = useMemo(
    () => !isEqual(initialSelectedOptions, selectedOptions),
    [initialSelectedOptions, selectedOptions],
  );

  const newLocationsValue = transformCodeNameModelsToSelectedLocations(selectedOptions);

  const onSubmit = (): void => {
    dispatch(changeNewProductField({ fieldName, selectedPermission, value: newLocationsValue }));
    dispatch(hideSecondaryPanel());
  };

  return (
    <ProductSecondaryPanel
      title="Select Location"
      isChange={isChange}
      isDisabled={
        isPanelConfirmDisabled({
          field: productLocations,
          selectedPermission,
          newFieldValue: newLocationsValue,
        }) || isReadOnly
      }
      customStyles={secondaryPanelLayoutStyles}
      onSubmit={onSubmit}
      onCancel={onCancel}
    >
      <SelectionPermissionDropdown
        selectedPermission={selectedPermission}
        setSelectedPermission={setSelectedPermission}
        permissionOptions={permissionOptions}
      />
      {isValueRequired(selectedPermission.code) && (
        <>
          <SectionDivider spaceY={4} />
          <div className="mb-2">
            <Search value={searchText} placeholder="Search locations" onChange={onFilterTextChange} />
          </div>
          {isFetchingReferences && <CircularLoadingSpinner />}
          {!isFetchingReferences && canDisplayOptionResults(filteredOptions) && (
            <LocationOptionsList
              filteredOptions={filteredOptions}
              selectedOptions={selectedOptions}
              openCategories={openCategories}
              sortDirection={sortDirection}
              includedOptions={includedOptions}
              excludedOptions={excludedOptions}
              onSort={onCategoryDirectionSort}
              onToggleCategory={onToggleCategory}
              setSelectedOptions={setSelectedOptions}
              getNumberOfFilteredOptions={getNumberOfFilteredOptions}
              isOptionSelected={isOptionSelected}
              isOptionIncluded={isOptionIncluded}
              onOptionIncludeExcludeChange={onOptionIncludeExcludeChange}
              onCategoryIncludeAll={onCategoryIncludeAll}
              onCategoryExcludeAll={onCategoryExcludeAll}
              getCategoryIncludeState={getCategoryIncludeState}
            />
          )}
          {!isFetchingReferences && !canDisplayOptionResults(filteredOptions) && searchText.length > 0 && (
            <p>There are no results matching your search</p>
          )}
        </>
      )}
    </ProductSecondaryPanel>
  );
};

export default ProductLocationsPanel;
